import React, { useState, useEffect } from 'react';
import usePageTitle from '../../../hooks/usePageTitle';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { faVideo } from '@fortawesome/free-solid-svg-icons';
import AuthLeftPanel from './AuthLeftPanel';
import { useTheme } from '../../../context/ThemeContext';
import RoleChoice from './RoleChoice';
import RegistrationPersonalInfo from './RegistrationPersonalInfo';
import RegistrationSuccess from './RegistrationSuccess';

function RegistrationForm() {
  usePageTitle('Sign Up');
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isLight = theme === 'light';

  const [step, setStep] = useState(1);
  const [role, setRole] = useState({ label: 'Filmmaker', value: 'Filmmaker', icon: faVideo });

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [gender, setGender] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [receiveNewsletter, setReceiveNewsletter] = useState(true);
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => { setError(''); }, [step]);

  const handleSubmit = async () => {
    const isValidEmail = /^([a-zA-Z\d.\-_]+)@([a-zA-Z\d-]+)\.([a-zA-Z]{2,12})(\.[a-zA-Z]{2,12})?$/;

    if (!firstName || !lastName || !email || !password || !confirmPassword || !birthDate || !role) {
      setError(t('All fields are required.')); return;
    }
    if (firstName.length < 2) { setError(t('First name must be at least 2 characters long.')); return; }
    if (lastName.length < 2)  { setError(t('Last name must be at least 2 characters long.')); return; }
    if (role.value === 'Actor' && !gender) { setError(t('Please select gender!')); return; }
    if (!isValidEmail.test(email)) { setError(t('Invalid Email')); return; }
    if (password !== confirmPassword) { setError(t('Passwords do not match.')); return; }
    if (new Date(birthDate) > new Date()) { setError(t('Birth date cannot be in the future.')); return; }
    if (password.length < 8) { setError(t('Password must be at least 8 characters long!')); return; }
    if (!agreeToTerms) { setError(t('Please check the user agreement option!')); return; }

    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/users/register`,
        { email, password, firstName, lastName, birthDate, role: role.value, receiveNewsletter, gender }
      );

      if (data.emailExists) { setError(t('Email is already in use.')); return; }

      if (data.reinstated) {
        toast.success(t("Welcome back! Your account has been reactivated. Please log in."));
        navigate('/login', { replace: true });
        return;
      }

      setError('');
      setSuccess(data.message);
      navigate('/verification', { state: { user: data.user }, replace: true });
    } catch (err) {
      let msg = err.response?.data?.message || err.response?.data?.error || t("An error occurred during registration.");
      if (msg.includes("validation failed") || msg.includes("birthDate")) msg = t("Please provide a valid birth date.");
      setError(msg);
    }
  };

  return (
    <div className="tw-flex tw-min-h-screen">
      <AuthLeftPanel />

      <div className={`tw-flex tw-flex-1 tw-flex-col tw-items-center tw-justify-center tw-overflow-y-auto tw-px-6 tw-pt-8 tw-pb-24 tw-transition-colors tw-duration-300 ${
        isLight ? 'tw-bg-[#F4F0FA]' : 'tw-bg-gradient-to-b tw-from-[#09010f] tw-to-[#1E0039]'
      }`}>
        <div className="tw-w-full tw-max-w-md">

          {/* Step indicator */}
          <div className="tw-mb-6 tw-flex tw-items-center tw-gap-2">
            {[1, 2].map((s) => (
              <div
                key={s}
                className={`tw-h-1 tw-flex-1 tw-rounded-full tw-transition-all ${s <= step ? 'tw-bg-[#FF43A7]' : (isLight ? 'tw-bg-[#E0D5F5]' : 'tw-bg-white/10')}`}
              />
            ))}
          </div>

          <div className={`tw-rounded-2xl tw-border tw-p-8 tw-shadow-[0_0_40px_rgba(255,67,167,0.08)] ${
            isLight ? 'tw-border-gray-200 tw-bg-white' : 'tw-border-[#FF43A7]/20 tw-bg-white/5'
          }`}>
            {step === 1 ? (
              <RoleChoice role={role} setRole={setRole} onNext={() => setStep(2)} />
            ) : (
              <>
                <RegistrationPersonalInfo
                  firstName={firstName} lastName={lastName} email={email}
                  password={password} confirmPassword={confirmPassword}
                  birthDate={birthDate} gender={gender} role={role}
                  setFirstName={setFirstName} setLastName={setLastName}
                  setEmail={setEmail} setPassword={setPassword}
                  setConfirmPassword={setConfirmPassword} setBirthDate={setBirthDate}
                  setGender={setGender} receiveNewsletter={receiveNewsletter}
                  setReceiveNewsletter={setReceiveNewsletter}
                  agreeToTerms={agreeToTerms} setAgreeToTerms={setAgreeToTerms}
                />

                {error && (
                  <p className="tw-mt-4 tw-text-center tw-text-sm tw-text-red-400">*{error}</p>
                )}
                {success && <RegistrationSuccess />}

                <div className="tw-mt-5 tw-flex tw-flex-col tw-gap-3">
                  <button
                    type="button"
                    onClick={handleSubmit}
                    className="tw-w-full tw-cursor-pointer tw-rounded-full tw-border-0 tw-bg-[#FF43A7] tw-py-3 tw-text-base tw-font-semibold tw-text-white tw-shadow-[0_0_20px_rgba(255,67,167,0.3)] tw-transition-all hover:tw-bg-[#FF43A7]/80"
                  >
                    {t("Sign Up")}
                  </button>
                  <button
                    type="button"
                    onClick={() => setStep(1)}
                    className="tw-w-full tw-cursor-pointer tw-rounded-full tw-border tw-border-[#FF43A7]/40 tw-bg-transparent tw-py-2.5 tw-text-sm tw-font-semibold tw-text-[#FF43A7] tw-transition-all hover:tw-bg-[#FF43A7]/10"
                  >
                    {t("Back")}
                  </button>
                </div>
              </>
            )}
          </div>

          <p className={`tw-mt-5 tw-text-center tw-text-sm ${isLight ? 'tw-text-gray-500' : 'tw-text-gray-500'}`}>
            {t("Already have an account?")}{" "}
            <Link to="/login" className="tw-text-[#FF43A7] hover:tw-underline">{t("Login here")}</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default RegistrationForm;
